/**
 * KYC cross-panel bridge (TASK-716).
 *
 * The KYC-DOMAIN coordination that survived kycChatStore's deletion, action
 * names unchanged so KYCWizardContext/KYCFormPanel/KYCWorkspace only swap the
 * import: the applied-batch seq signal (kept VERBATIM — the form-sync
 * mechanism), current section, form-data snapshot + live reader, and the
 * cross-panel goToStep/applyProposal slots. Chat transport/state lives in the
 * dock's kyc-scope store.
 */

import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

export interface ExtractionProposalItem {
  field_path: string;
  value: unknown;
  confidence: number;
  source?: string;
  // TASK-479: position-classification proposals (field_path `position.<id>.sub_asset_class`)
  // carry the position id + a display label ("<security> → <SAC>").
  position_id?: string;
  label?: string;
}

/** Batch of proposals to apply to the form */
export interface AppliedProposalBatch {
  items: Array<{ fieldPath: string; value: unknown }>;
  /** Monotonic counter — lets subscribers detect new batches */
  seq: number;
}

interface KYCBridgeState {
  currentSection: string | null;
  /** Batch signal for KYCWizardContext to subscribe to */
  appliedBatch: AppliedProposalBatch | null;
  /** Snapshot of current form data for AI context */
  formDataSnapshot: Record<string, unknown> | null;

  _goToStepFn: ((step: number) => void) | null;
  _applyProposalFn: ((fieldPath: string, value: unknown) => void) | null;
  _getFormDataSnapshotFn: (() => Record<string, unknown>) | null;
  /** TASK-716: dirty-state reader for the focus popup's close guard. */
  _isDirtyFn: (() => boolean) | null;

  setCurrentSection: (section: string | null) => void;
  applyProposalsToForm: (items: Array<{ fieldPath: string; value: unknown }>) => void;
  setFormDataSnapshot: (data: Record<string, unknown>) => void;
  setGoToStep: (fn: ((step: number) => void) | null) => void;
  goToStep: (step: number) => void;
  setApplyProposal: (fn: ((fieldPath: string, value: unknown) => void) | null) => void;
  applyProposal: (fieldPath: string, value: unknown) => void;
  setGetFormDataSnapshot: (fn: (() => Record<string, unknown>) | null) => void;
  getFormDataSnapshotLive: () => Record<string, unknown> | null;
  setIsDirty: (fn: (() => boolean) | null) => void;
  isDirty: () => boolean;
  reset: () => void;
}

// Module-level, deliberately NOT reset — keeps the seq guard sound across resets.
let _appliedSeq = 0;

const initialState = {
  currentSection: null as string | null,
  appliedBatch: null as AppliedProposalBatch | null,
  formDataSnapshot: null as Record<string, unknown> | null,
  _goToStepFn: null as ((step: number) => void) | null,
  _applyProposalFn: null as ((fieldPath: string, value: unknown) => void) | null,
  _getFormDataSnapshotFn: null as (() => Record<string, unknown>) | null,
  _isDirtyFn: null as (() => boolean) | null,
};

export const useKYCBridge = create<KYCBridgeState>()(
  devtools(
    (set, get) => ({
      ...initialState,

      setCurrentSection: (section) => set({ currentSection: section }),

      applyProposalsToForm: (items) => set({ appliedBatch: { items, seq: ++_appliedSeq } }),

      setFormDataSnapshot: (data) => set({ formDataSnapshot: data }),

      setGoToStep: (fn) => set({ _goToStepFn: fn }),
      goToStep: (step) => get()._goToStepFn?.(step),
      setApplyProposal: (fn) => set({ _applyProposalFn: fn }),
      applyProposal: (fieldPath, value) => get()._applyProposalFn?.(fieldPath, value),
      setGetFormDataSnapshot: (fn) => set({ _getFormDataSnapshotFn: fn }),
      getFormDataSnapshotLive: () => {
        const state = get();
        return state._getFormDataSnapshotFn ? state._getFormDataSnapshotFn() : state.formDataSnapshot;
      },
      setIsDirty: (fn) => set({ _isDirtyFn: fn }),
      isDirty: () => get()._isDirtyFn?.() ?? false,

      reset: () => set(initialState),
    }),
    { name: 'KYCBridge' }
  )
);
